import { Info, KeyboardArrowDown } from '@mui/icons-material'
import { Box, ClickAwayListener, Collapse, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { ReactElement, useState } from 'react'

type CollapsibleSectionProps = {
  text: string
  info?: string | ReactElement
  contextHelpText?: string | ReactElement
}

const CollapsibleSection = ({
  children,
  text,
  contextHelpText,
  info,
}: React.PropsWithChildren<CollapsibleSectionProps>) => {
  const [open, setOpen] = useState(true)
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handleClick = () => {
    setTooltipOpen(prev => !prev)
  }

  const handleClickAway = () => {
    setTooltipOpen(false)
  }

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="baseline" gap={2}>
          <Typography
            variant="h6"
            component="span"
            color="text.mediumEmphasis"
            sx={{ pl: useMediaQuery(useTheme().breakpoints.down('sm')) ? theme.spacing(2) : theme.spacing(3) }}
          >
            {text}
          </Typography>
          {contextHelpText && (
            isDownSm ? (
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box>
                  <Tooltip
                    title={
                      <Typography sx={{ whiteSpace: 'pre-line' }} variant="caption">
                        {contextHelpText}
                      </Typography>
                    }
                    placement="bottom"
                    open={tooltipOpen}
                    disableHoverListener
                    disableFocusListener
                    disableTouchListener
                  >
                    <Info onClick={handleClick} sx={{ fontSize: 16 }} />
                  </Tooltip>
                </Box>
              </ClickAwayListener>
            ) : (
              <Tooltip
                title={
                  <Typography sx={{ whiteSpace: 'pre-line' }} variant="caption">
                    {contextHelpText}
                  </Typography>
                }
                placement="bottom"
              >
                <Info sx={{ fontSize: 16 }} />
              </Tooltip>
            )
          )}

          {info && (
            <Typography variant="body1" fontWeight={600} component="span" color="primary.main">
              {info}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={() => setOpen(!open)} aria-label={text}>
          <KeyboardArrowDown
            sx={{
              color: 'text.dark',
              transition: 'all 150ms ease-in-out',
              transform: open ? 'rotate(-180deg)' : '',
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack>{children}</Stack>
      </Collapse>
    </Stack>
  )
}

export default CollapsibleSection